import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  IconButton,
  TextField,
  Stack,
  Button,
  useTheme,
  useMediaQuery,
  Pagination,
  MenuItem,
  Select,
  FormControl,
  InputAdornment,
  Fab,
  Divider,
} from "@mui/material";
import { Edit, Delete, Print, Search } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useDelete from "../../../../api/useDelete";
import { useTranslation } from "react-i18next";
import PrintPayment from "./PrintPayment";
import EditCollectHtPatientFeesDialog from "./EditCollectHtPatientFeesDialog";
import { useNavigate } from "react-router-dom";

const CollectHtPatientFeesList = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [openDialog, setOpenDialog] = useState(false);
  const [currentPayment, setCurrentPayment] = useState(null);
  const [printData, setPrintData] = useState(null);
  const printRef = useRef();

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [query, setQuery] = useState("");
  const [dateRangeFilter, setDateRangeFilter] = useState("today");

  // Pagination States
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalHtPatientPayment, setTotalHtPatientPayment] = useState(0);

  // Building query for backend
  useEffect(() => {
    let q = "";
    if (searchTerm) q += `search=${encodeURIComponent(searchTerm)}`;

    // Handle custom date range
    if (dateRangeFilter === "custom") {
      if (startDate) q += `${q ? "&" : ""}start_datetime=${startDate}T00:00:00`;
      if (endDate) q += `${q ? "&" : ""}end_datetime=${endDate}T23:59:59`;
    } else {
      // Map frontend date_range to backend type parameter
      const typeMapping = {
        today: "daily",
        week: "weekly",
        month: "monthly",
        year: "yearly",
        custom: "",
      };
      const backendType = typeMapping[dateRangeFilter];
      if (backendType) {
        q += `${q ? "&" : ""}type=${backendType}`;
      }
    }
    setQuery(q);
  }, [searchTerm, startDate, endDate, dateRangeFilter]);

  const {
    data: payments,
    refetch,
    isLoading,
    isError,
  } = useFetchObjects(
    ["ht-payment", query, page, rowsPerPage],
    `ht-payments/?page=${page}&page_size=${rowsPerPage}&${query}`,
    token
  );

  const { handleDelete, ConfirmDialog } = useDelete("ht-payments", token);

  useEffect(() => {
    refetch();
  }, [query, refetch, page, rowsPerPage]);

  useEffect(() => {
    if (payments && payments.count !== undefined) {
      setTotalHtPatientPayment(payments.count);
    }
  }, [payments]);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleClearSearch = () => setSearchTerm("");
  const handleClearDates = () => {
    setStartDate("");
    setEndDate("");
  };

  // Calculate totals by currency for HT patient payments
  const totalsByCurrency =
    payments?.results?.reduce((acc, payment) => {
      const currency = payment.display_ht_patient?.currency || 1; // Default to AFN
      // Note: advance_amount and amount use the same currency

      // Add main payment amount (only if not null)
      if (payment.amount !== null && payment.amount !== undefined) {
        if (!acc[currency]) acc[currency] = { amount: 0, advance: 0 };
        acc[currency].amount += Number(payment.amount || 0);
      }

      // Add advance amount (only if not null) - uses same currency
      if (
        payment.advance_amount !== null &&
        payment.advance_amount !== undefined
      ) {
        if (!acc[currency]) acc[currency] = { amount: 0, advance: 0 };
        acc[currency].advance += Number(payment.advance_amount || 0);
      }

      return acc;
    }, {}) || {};

  const pageCount = payments?.results?.length || 0;

  const handleEditClick = (payment) => {
    setCurrentPayment(payment);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPayment(null);
  };

  const handlePrintClick = (payment) => {
    setPrintData(payment);
    setTimeout(() => {
      if (printRef.current) {
        const printWindow = window.open("", "_blank");
        printWindow.document.write(printRef.current.innerHTML);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }
    }, 500);
  };

  const getCurrencySymbol = (currency) => {
    switch (currency) {
      case 1:
        return "؋";
      case 2:
        return "$";
      case 3:
        return "€";
      default:
        return "";
    }
  };

  return (
    <Grid container spacing={4} mb={4} pb={5}>
      <Grid item xs={12}>
        <Box
          padding="25px"
          backgroundColor="white"
          boxShadow={3}
          borderRadius={2}
        >
          {/* Title */}
          <Box display="flex" justifyContent="space-between" mb={4}>
            <Typography variant="h5" fontWeight={700} color="primary">
              {t("reception.htPatientPaymentList")}
            </Typography>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate("/reception/collect-ht-patient-fees/add")}
              sx={{
                textTransform: "none",
                fontWeight: "bold",
                paddingX: 3,
                paddingY: isSmallScreen ? 1 : 1.5,
                alignSelf: isSmallScreen ? "flex-end" : "center",
                borderRadius: "8px",
                boxShadow: 3,
                transition: "all 0.3s ease",
                backgroundColor: theme.palette.primary,
                "&:hover": {
                  backgroundColor: theme.palette.primary,
                  boxShadow: 6,
                  transform: "translateY(-2px)",
                },
                "&:active": {
                  boxShadow: 2,
                  transform: "translateY(0px)",
                },
                "& .MuiButton-startIcon": {
                  marginRight: 1,
                },
              }}
            >
              {t("reception.addNew")}
            </Button>
          </Box>

          {/* Enhanced Filters */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            mb={3}
            justifyContent="space-between"
          >
            <TextField
              label={t(
                "reception.searchByProcedure",
                "Search by Procedure or Phone Number"
              )}
              variant="outlined"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="primary" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearSearch} size="small">
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                bgcolor: "#fff",
                borderRadius: 2,
                boxShadow: 1,
              }}
            />
            <FormControl
              variant="outlined"
              size="small"
              sx={{
                minWidth: 180,
                bgcolor: "#fff",
                borderRadius: 2,
                boxShadow: 1,
              }}
            >
              <Select
                value={dateRangeFilter}
                onChange={(e) => setDateRangeFilter(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <CalendarTodayIcon color="primary" />
                  </InputAdornment>
                }
              >
                <MenuItem value="today">
                  {t("reception.today", "Today")}
                </MenuItem>
                <MenuItem value="week">
                  {t("reception.thisWeek", "This Week")}
                </MenuItem>
                <MenuItem value="month">
                  {t("reception.thisMonth", "This Month")}
                </MenuItem>
                <MenuItem value="year">
                  {t("reception.thisYear", "This Year")}
                </MenuItem>
                <MenuItem value="custom">
                  {t("reception.customRange", "Custom Range")}
                </MenuItem>
              </Select>
            </FormControl>
            {dateRangeFilter === "custom" && (
              <>
                <TextField
                  type="date"
                  variant="outlined"
                  size="small"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputProps={{
                    endAdornment: startDate && (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setStartDate("")}
                          size="small"
                        >
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: 180,
                    bgcolor: "#fff",
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                  label={t("reception.startDate", "Start Date")}
                />
                <TextField
                  type="date"
                  variant="outlined"
                  size="small"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputProps={{
                    endAdornment: endDate && (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setEndDate("")} size="small">
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    minWidth: 180,
                    bgcolor: "#fff",
                    borderRadius: 2,
                    boxShadow: 1,
                  }}
                  label={t("reception.endDate", "End Date")}
                />
                {(startDate || endDate) && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleClearDates}
                    startIcon={<ClearIcon />}
                    sx={{
                      minWidth: 120,
                      bgcolor: "#fff",
                      borderRadius: 2,
                      boxShadow: 1,
                    }}
                  >
                    {t("reception.clearDates", "Clear Dates")}
                  </Button>
                )}
              </>
            )}
          </Stack>

          {/* Table */}
          {isLoading ? (
            <Box textAlign="center" mt={4}>
              <CircularProgress />
              <Typography variant="body2">{t("reception.loading")}</Typography>
            </Box>
          ) : isError ? (
            <Box textAlign="center" color="red" mt={4}>
              <Typography variant="body2">
                {t("reception.errorFetchingData")}
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 3,
                borderRadius: 2,
                overflowX: "auto", // Allow horizontal scroll if needed
              }}
            >
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f9f9f9" }}>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Typography noWrap>#</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Typography noWrap>{t("reception.patient")}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Typography noWrap>
                        {t("reception.contact", "Contact")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Typography noWrap>
                        {t("reception.procedureType")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Typography noWrap>
                        {t("reception.totalPrice")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Typography noWrap>
                        {t("call_center.discount")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Typography noWrap>
                        {t("reception.advancePaid", "Advance Paid")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Typography noWrap>
                        {t("reception.amountPaid")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Typography noWrap>{t("reception.totalPaid")}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Typography noWrap>
                        {t("reception.balanceDue")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Typography noWrap>
                        {t("reception.paymentDate")}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                      <Typography noWrap>{t("reception.actions")}</Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                  {payments?.results?.map((payment, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        "&:nth-of-type(odd)": { backgroundColor: "#f5f5f5" },
                      }}
                    >
                      <TableCell align="center">
                        <Typography>{payment.id}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment.display_ht_patient?.patient?.name || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment.display_ht_patient?.patient?.phone || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment.display_ht_patient?.type || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment?.display_ht_patient?.total_cost}{" "}
                          {getCurrencySymbol(
                            payment.display_ht_patient?.currency
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment?.display_ht_patient?.discount} %
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment?.advance_amount}{" "}
                          {getCurrencySymbol(
                            payment.display_ht_patient?.currency
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment?.amount}{" "}
                          {getCurrencySymbol(
                            payment.display_ht_patient?.currency
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment?.display_ht_patient?.total_paid}{" "}
                          {getCurrencySymbol(
                            payment.display_ht_patient?.currency
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {payment?.display_ht_patient?.balance_due}{" "}
                          {getCurrencySymbol(
                            payment.display_ht_patient?.currency
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography>
                          {new Date(payment.date).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          onClick={() => handleEditClick(payment)}
                          color="primary"
                          size="small"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(payment.id)}
                          color="error"
                          size="small"
                        >
                          <Delete />
                        </IconButton>
                        <IconButton
                          onClick={() => handlePrintClick(payment)}
                          color="primary"
                          size="small"
                        >
                          <Print />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {/* Pagination Controls */}
              <Grid item xs={12} m={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mt={4}
                  mb={2}
                >
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" mr={2}>
                      {t("reception.rowsPerPage")}:
                    </Typography>
                    <FormControl variant="standard" sx={{ minWidth: 120 }}>
                      <Select
                        value={rowsPerPage}
                        onChange={handleRowsPerPageChange}
                        label={t("reception.rowsPerPage")}
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                        <MenuItem value={100}>100</MenuItem>
                        <MenuItem value={500}>500</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Pagination
                    count={Math.ceil(totalHtPatientPayment / rowsPerPage)}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    variant="outlined"
                    shape="rounded"
                  />
                </Box>
              </Grid>
            </TableContainer>
          )}

          {/* Edit Dialog */}
          {currentPayment && (
            <EditCollectHtPatientFeesDialog
              open={openDialog}
              onClose={handleCloseDialog}
              payment={currentPayment}
              token={token}
            />
          )}

          {/* Print Component */}
          {printData && (
            <div ref={printRef} style={{ display: "none" }}>
              <PrintPayment data={printData} />
            </div>
          )}

          {/* Multi-Currency Total Display */}
          {pageCount > 0 && (
            <Box
              sx={{
                position: "fixed",
                bottom: 20,
                right: 20,
                zIndex: 1000,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  boxShadow: 3,
                  minWidth: 250,
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  HT Patient Payments Summary:
                </Typography>

                {/* Multi-currency breakdown */}
                {Object.entries(totalsByCurrency)
                  .filter(
                    ([currency, totals]) =>
                      totals.amount > 0 || totals.advance > 0
                  )
                  .map(([currency, totals]) => (
                    <Box key={currency} mb={1}>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary.main"
                      >
                        {getCurrencySymbol(parseInt(currency))} Currency:
                      </Typography>
                      {totals.amount > 0 && (
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          ml={2}
                        >
                          <Typography variant="body2" color="success.main">
                            Payments:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="success.main"
                          >
                            {totals.amount.toLocaleString()}{" "}
                            {getCurrencySymbol(parseInt(currency))}
                          </Typography>
                        </Box>
                      )}
                      {totals.advance > 0 && (
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          ml={2}
                        >
                          <Typography variant="body2" color="warning.main">
                            Advances:
                          </Typography>
                          <Typography
                            variant="body2"
                            fontWeight="bold"
                            color="warning.main"
                          >
                            {totals.advance.toLocaleString()}{" "}
                            {getCurrencySymbol(parseInt(currency))}
                          </Typography>
                        </Box>
                      )}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        ml={2}
                        mb={1}
                      >
                        <Typography variant="body2" color="primary.main">
                          Total:
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="primary.main"
                        >
                          {(totals.amount + totals.advance).toLocaleString()}{" "}
                          {getCurrencySymbol(parseInt(currency))}
                        </Typography>
                      </Box>
                    </Box>
                  ))}

                <Divider sx={{ my: 1 }} />
                <Box display="flex" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Total Records ({pageCount}):
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="primary.main"
                  >
                    {pageCount} payments
                  </Typography>
                </Box>

                {/* Currency Summary */}
                <Box mt={1}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Currency Summary:
                  </Typography>
                  {Object.entries(totalsByCurrency)
                    .filter(
                      ([currency, totals]) =>
                        totals.amount > 0 || totals.advance > 0
                    )
                    .map(([currency, totals]) => (
                      <Box
                        key={currency}
                        display="flex"
                        justifyContent="space-between"
                        mb={0.5}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {getCurrencySymbol(parseInt(currency))} Total:
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight="bold"
                          color="primary.main"
                        >
                          {(totals.amount + totals.advance).toLocaleString()}{" "}
                          {getCurrencySymbol(parseInt(currency))}
                        </Typography>
                      </Box>
                    ))}
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      </Grid>
      <ConfirmDialog />
    </Grid>
  );
};

export default CollectHtPatientFeesList;
